import React, { useCallback, useEffect, useRef, useState } from 'react';
import { select, line, zoom, curveStepAfter, zoomIdentity, Selection, ZoomBehavior } from 'd3';
import { hierarchy, tree, HierarchyPointNode } from 'd3-hierarchy';
import { isNull } from 'lodash/fp';

import styles from './DocumentName.module.scss';

const { french, grey, frenchLight, fontSize, fontFamily, frenchDark, white } = styles;

// Create the line for each link, to create an 'elbow' line we will need three sets
// of coordinates to plot to two vertical and one horizontal lines
interface NodeCoordinates {
    x: number;
    y: number;
}

export interface TreeData {
    name: string;
    parent: string | null;
    agreementTypeId: number;
    parentAgreementTypeId: number | null;
    children?: TreeData[];
    x0?: number;
    y0?: number;
}

interface AgreementFamilyTreeChartProps {
    height: number;
    width: number;
    treeData: TreeData[];
    resetZoom: boolean;
    zoomSteps: number;
    zoomUpdated: boolean;
}

const NODE_DIMENSIONS = 300;
const NODE_SPACING_AND_LINES = 100;
const ULTIMATE_PARENT_NAME = 'Ark-51';

export const AgreementFamilyTreeChart: React.FC<AgreementFamilyTreeChartProps> = ({
    height,
    width,
    treeData,
    resetZoom,
    zoomSteps,
    zoomUpdated
}) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const gRef = useRef<SVGGElement | null>(null);
    const [selectedAgreement, setSelectedAgreement] = useState<HierarchyPointNode<TreeData> | null>(null);
    const zoomBehavior = useRef<ZoomBehavior<SVGSVGElement, unknown>>();

    const initialScale = 0.4;
    const initialTranslateX = (width / 2) - 100;
    const initialTranslateY = (height / 2) - 200;

    const elbowLines = (childCoordinates: NodeCoordinates, parentCoordinates: NodeCoordinates | null) => {
        if (!parentCoordinates) {
            return 'M0,0L0,0';
        }
        let difference = childCoordinates.y - parentCoordinates.y;
        let newY = parentCoordinates.y + difference * 0.50;

        let lineData: [number, number][] = [[
            parentCoordinates.x,
            parentCoordinates.y
        ], [
            parentCoordinates.x,
            newY
        ], [
            childCoordinates.x,
            childCoordinates.y
        ]];
        const drawLines = line().x(d => d[0]).y(d => d[1]).curve(curveStepAfter);
        return drawLines(lineData);
    };

    useEffect(() => {
        const svg = select<SVGSVGElement, unknown>(svgRef.current!);

        // Declares the tree layout and assigns the size and spacing of the nodes
        const treeMap = tree<TreeData>().nodeSize([NODE_DIMENSIONS + NODE_SPACING_AND_LINES, NODE_DIMENSIONS + NODE_SPACING_AND_LINES])
            .separation((a, b) => {
                return a.parent == b.parent ? 1 : 1.25;
            });

        // Maps the node data to the tree layout
        const nodes = hierarchy(treeData[0], d => d.children);
        const positionedNodes = treeMap(nodes); // Now nodes with x and y
        const allNodes = positionedNodes.descendants() as HierarchyPointNode<TreeData>[];

        const g = svg.append('g');
        gRef.current = g.node();

        // Create the primary links from the tree data
        g.selectAll('.line')
            .data(allNodes.slice(1))
            .enter().append('path')
            .attr('class', 'agreement-links')
            .style('stroke', french)
            .style('fill', 'none')
            .style('stroke-width', '4px')
            .attr('d', ({ x, y, parent, depth }) => {
                const childCoordinates = { x, y };
                const parentCoordinates = (!parent || depth === 1) ? null : { x: parent.x, y: parent.y };
                return elbowLines(childCoordinates, parentCoordinates);
            });

        const node = g.selectAll('.node')
            .data(allNodes)
            .enter().append('g')
            .attr('class', ({ children }) => 'node' + (children ? ' node--internal' : ' node--leaf'))
            .attr('transform', ({ x, y }) => 'translate(' + x + ',' + y + ')');

        node.append('rect')
            .attr('height', NODE_DIMENSIONS)
            .attr('width', NODE_DIMENSIONS)
            .attr('class', ({ data: { name } }) => name)
            .attr('x', -(NODE_DIMENSIONS / 2))
            .attr('y', -(NODE_DIMENSIONS / 2))
            .attr('rx', 8)
            .attr('ry', 8)
            .style('fill', ({ data: { name } }) => name === ULTIMATE_PARENT_NAME ? 'none' : french);

        /* eslint-disable @typescript-eslint/no-explicit-any */
        // This is so we can spread the agreement name over multiple lines if needs be
        const wrapText = (text: Selection<SVGTextElement, any, any, any>) => {
            text.each(({ data: { name } }, i, nodes) => {
                const current = nodes[i];
                const words = name.split(/\s+/).reverse();
                let word;
                let line: string[] = [];
                const lineHeight = 1.25;
                const y = parseFloat(select(current).attr('y') || '0');
                const dy = parseFloat(select(current).attr('dy') || '0');
                let lineNumber = 0;

                const textSpans: SVGTSpanElement[] = [];

                let tspan = select(current)
                    .text(null)
                    .append('tspan')
                    .attr('x', 0)
                    .attr('y', y)
                    .attr('dy', `${dy}em`);

                // Keep track of all the new lines we have to add so we can adjust the spacing to centre it
                textSpans.push(tspan.node() as SVGTSpanElement);

                // Go through each word, add it to the current text line
                // check if that is greater than the node width - padding
                // if so start a new line
                while ((word = words.pop())) {
                    line.push(word);
                    tspan.text(line.join(' '));
                    if ((tspan.node() as SVGTextContentElement).getComputedTextLength() > NODE_DIMENSIONS - 10) {
                        line.pop();
                        tspan.text(line.join(' '));
                        line = [word];
                        lineNumber++;
                        tspan = select(current)
                            .append('tspan')
                            .attr('x', 0)
                            .attr('y', y)
                            .attr('dy', `${lineNumber * lineHeight + dy}em`)
                            .text(word);
                        textSpans.push(tspan.node() as SVGTSpanElement);
                    }
                }

                // Now that we have all the lines of text we need to go through and bump them up
                // so that the text is centred in the rect based on the number of lines we have split it over
                const totalLines = textSpans.length;
                const offset = ((totalLines - 1) * lineHeight) / 2;
                textSpans.forEach(tspan => {
                    const currentDy = parseFloat(tspan.getAttribute('dy') || '0');
                    tspan.setAttribute('dy', `${currentDy - offset}em`);
                });

            });
        };

        node.append('text')
            .attr('x', 0)
            .attr('y', 0)
            .attr('dy', '.35em')
            .attr('text-anchor', 'middle')
            .attr('class', ({ data: { name } }) => name)
            .style('fill', ({ data: { name } }) => name === ULTIMATE_PARENT_NAME ? 'none' : white)
            .style('font-weight', 'bold')
            .style('font-size', fontSize)
            .style('font-family', fontFamily)
            .text(({ data: { name } }) => name)
            .call(wrapText);

        return () => {
            // Anything added to the svg on mount via .append must be removed on unmount to stop new elements being added each render
            g.selectAll('.node').remove();
            g.selectAll('.line').remove();
            g.selectAll('.agreement-links').remove();
            g.selectAll().remove();
        };

    }, [treeData, height, width]);

    const updateNodeFills = useCallback((svg: Selection<SVGSVGElement, unknown, null, undefined>) => {
        // Get the svg nodes and links
        const nodes = svg.selectAll<SVGGElement, HierarchyPointNode<TreeData>>('g.node');
        const links = svg.selectAll<SVGPathElement, HierarchyPointNode<TreeData>>('path.agreement-links');

        const selectedNodeChildAgreements = isNull(selectedAgreement) ? [] : (selectedAgreement.children?.map(({ data: { name } }) => name) || []);

        // Update the fill of the nodes
        nodes.select('rect')
            .style('fill', ({ data: { name } }) => {
                if (name === ULTIMATE_PARENT_NAME) {
                    return 'none';
                }
                if (selectedNodeChildAgreements.includes(name)) {
                    return frenchLight;
                }
                if (name === selectedAgreement?.data.name) {
                    return frenchDark;
                }
                return french;
            });

        // Update the stroke colour for the links
        if (!isNull(selectedAgreement) && selectedNodeChildAgreements.length > 0) {
            links
                .style('stroke', ({ data: { name } }) => {
                    if (name === ULTIMATE_PARENT_NAME) {
                        return 'none';
                    }
                    if (selectedNodeChildAgreements.includes(name) || name === selectedAgreement?.data.name) {
                        return frenchDark;
                    }
                    return french;
                });
        } else {
            links
                .style('stroke', french);
        }
    }, [selectedAgreement]);

    // Update node fills has its own use effect so the changes don't re-render the zoom etc on mouse events
    useEffect(() => {
        setTimeout(() => {
            const svg = select<SVGSVGElement, unknown>(svgRef.current!);
            svg.selectAll<SVGGElement, HierarchyPointNode<TreeData>>('g.node')
                .on('mouseover', (_, d) => setSelectedAgreement(d))
                .on('mouseout', () => setSelectedAgreement(null));

            updateNodeFills(svg);
        }, 150);
    }, [setSelectedAgreement, updateNodeFills, resetZoom, zoomUpdated, zoomSteps, width, height]);

    // Initial zoom behavior
    useEffect(() => {
        if (!svgRef.current) {
            return;
        }

        const svg = select(svgRef.current);

        // Only run this once 'on mount'
        if (!zoomBehavior.current) {
            zoomBehavior.current = zoom<SVGSVGElement, unknown>()
                .scaleExtent([0.2, 5])
                .on('zoom', ({ transform }) => {
                    if (gRef.current) {
                        select(gRef.current).attr('transform', transform);
                    }
                });
            svg.call(zoomBehavior.current);
            svg.transition().call(
                zoomBehavior.current.transform,
                zoomIdentity.translate(initialTranslateX, initialTranslateY).scale(initialScale)
            );
        }

        // On mount or size change, reset transform to initial
        svg.transition().call(
            zoomBehavior.current.transform,
            zoomIdentity.translate(initialTranslateX, initialTranslateY).scale(initialScale)
        );
    }, [width, height, initialTranslateX, initialTranslateY, initialScale]);

    // Handle resetZoom, zoomIn, zoomOut as separate use effect to avoid causing the initial logic to run
    useEffect(() => {
        if (!svgRef.current || !zoomBehavior.current) {
            return;
        }

        const svg = select(svgRef.current);

        if (resetZoom) {
            svg.transition().call(
                zoomBehavior.current.transform,
                zoomIdentity.translate(initialTranslateX, initialTranslateY).scale(initialScale)
            );
        } else if (zoomUpdated) {
            if (zoomSteps > 0) {
                svg.transition().call(zoomBehavior.current.scaleBy, 1.2);
            } else if (zoomSteps < 0) {
                svg.transition().call(zoomBehavior.current.scaleBy, 0.8);
            }
        }
    }, [resetZoom, zoomUpdated, zoomSteps, width, height, initialTranslateX, initialTranslateY, initialScale]);

    return (
        <div style={{ height, width, border: `${grey} solid 1px`, borderRadius: '5px' }}>
            <svg ref={svgRef} style={{ height, width, overflow: 'hidden', borderRadius: '5px', cursor: 'pointer' }} />
        </div>
    );
};
